import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Divider, Select, MenuItem, FormControl, Paper } from '@esgian/esgianui';
import TableV2 from '@components/Tables/TableV2';
import { getLatestPortCalls } from '@api';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '@constants';
import { GEOGRAPHICAL_TYPES } from '@constants';
import { ProfileLink } from '@components/Links';
import { formatNumberReadable } from '@helpers';

const FleetTopPortsTable = ({ vesselImos, segmentId, setPreviousDays }) => {
  const [portPeriod, setPortPeriod] = useState(30); // Default to 30 days

  // Get port calls for the selected vessels
  const portCallsQuery = useQuery({
    queryKey: ['fleetPortCalls', vesselImos, segmentId, portPeriod, vesselImos.length],
    queryFn: ({ signal }) => {
      if (!vesselImos.length) return [];

      const startDate = moment()
        .subtract(portPeriod, 'days')
        .startOf('day')
        .format(DATE_TIME_FORMAT);
      const endDate = moment().add(1, 'day').startOf('day').format(DATE_TIME_FORMAT);

      return getLatestPortCalls(
        {
          geographicalType: GEOGRAPHICAL_TYPES.PORT,
          segmentTypeId: segmentId,
          numberOfPortCalls: 1000, // Large enough to get all port calls
          imos: vesselImos,
          startDate,
          endDate
        },
        signal
      );
    },
    enabled: vesselImos.length > 0
  });

  const topPorts = useMemo(() => {
    if (!portCallsQuery.data?.length) return [];

    const portCallsCount = {};
    portCallsQuery.data.forEach((call) => {
      if (!call.portId) return;

      const portKey = `${call.portId}`;
      if (!portCallsCount[portKey]) {
        portCallsCount[portKey] = {
          portId: call.portId,
          portName: call.portName,
          portUnlocode: call.portUnlocode,
          countryName: call.countryName,
          portCallsCount: 0
        };
      }
      portCallsCount[portKey].portCallsCount++;
    });

    return Object.values(portCallsCount)
      .sort((a, b) => b.portCallsCount - a.portCallsCount)
      .slice(0, 10);
  }, [portCallsQuery.data]);

  const topPortsColumns = useMemo(
    () => [
      {
        name: 'portId',
        options: {
          display: false,
          viewColumns: false,
          filter: false
        }
      },
      {
        name: 'portName',
        label: 'Port Name',
        options: {
          sort: true,
          sortThirdClickReset: true,
          filter: false,
          setCellProps: () => ({
            style: {
              width: '40%'
            }
          }),
          customBodyRender: (value, tableMeta) => (
            <ProfileLink profile={'port'} id={tableMeta.rowData[0]} name={value} />
          )
        }
      },
      {
        name: 'portCallsCount',
        label: `Last ${portPeriod} days`,
        options: {
          sort: true,
          sortThirdClickReset: true,
          filter: false,
          setCellProps: () => ({
            style: {
              width: '30%',
              textAlign: 'center'
            }
          }),
          customBodyRender: (value) => (
            <Typography variant="body2" align="center">
              {formatNumberReadable(value.toString())}
            </Typography>
          )
        }
      }
    ],
    [portPeriod]
  );

  const topPortsData = useMemo(() => {
    return topPorts.map((port) => ({
      portId: port.portId,
      portName: port.portName || 'Unknown',
      portCallsCount: port.portCallsCount
    }));
  }, [topPorts]);

  const handlePeriodChange = useCallback((event) => {
    setPortPeriod(Number(event.target.value));
    setPreviousDays(Number(event.target.value));
  }, []);

  const isLoading = portCallsQuery.isLoading;

  return (
    <Paper>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6">Top Visited Ports</Typography>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <Select value={portPeriod} onChange={handlePeriodChange} size="small" displayEmpty>
              <MenuItem value={5}>Last 5 days</MenuItem>
              <MenuItem value={10}>Last 10 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={60}>Last 60 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider />

        {isLoading ? (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Loading port calls...
            </Typography>
          </Box>
        ) : topPortsData.length > 0 ? (
          <TableV2
            data={topPortsData}
            columns={topPortsColumns}
            options={{
              selectableRows: 'none',
              filter: false,
              search: false,
              download: false,
              print: false,
              viewColumns: false,
              elevation: 0,
              pagination: false,
              setTableProps: () => ({ size: 'small' }),
              customFooter: () => null,
              sortOrder: {
                name: 'portCallsCount',
                direction: 'desc'
              }
            }}
          />
        ) : (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No port calls found in the selected period
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

FleetTopPortsTable.propTypes = {
  vesselImos: PropTypes.arrayOf(PropTypes.number).isRequired,
  segmentId: PropTypes.number.isRequired,
  setPreviousDays: PropTypes.func.isRequired
};

export default FleetTopPortsTable;
